// components/AML/AMLMonthlyExportButton.jsx
import React, { useMemo, useState } from "react";
import { buildStatementWorkbook, getSummaryKeyEmail, isSimpleOrPepsCheck } from "../../../backend/monthlyStatements/workbook";
import { saveAs } from "file-saver";
import {
  collectionGroup,
  collection,
  query,
  where,
  orderBy,
  getDocs,
  limit,
} from "firebase/firestore";
import { dbAML as db } from "../../../backend/firebaseConfig";
import ButtonGreen from "../../DesignSystem/Buttons/ButtonGreen";
import dayjs from "dayjs";
import {
  getCheckPrice,
  normaliseCheckType,
} from "./Utils/checkPricing";

function getMonthBounds(yyyyMm) {
  const [y, m] = yyyyMm.split("-").map(Number);
  const start = new Date(Date.UTC(y, m - 1, 1, 0, 0, 0));
  const end = new Date(Date.UTC(y, m, 1, 0, 0, 0)); // first of next month
  return { start, end };
}

export default function AMLMonthlyExportButton({
  month,
  splitByType = false,
  filenamePrefix = "check² Monthly Statement",
  createdAtType = "iso",
  mapRow,
}) {
  const [loading, setLoading] = useState(false);

  const requestDataSubcollectionNames = useMemo(
    () => ["Request Data", "RequestData", "requestData", "request data"],
    [],
  );

  const handleDownload = async () => {
    try {
      if (!month || !/^\d{4}-\d{2}$/.test(month)) {
        alert("Please choose a month (YYYY-MM).");
        return;
      }

      setLoading(true);

      const hasSubcollection = async (docRef, subcollectionNames) => {
        for (const name of subcollectionNames) {
          try {
            const subref = collection(docRef, name.trim());
            const s = await getDocs(query(subref, limit(1)));
            if (!s.empty) return true;
          } catch (_) {
            // ignore
          }
        }
        return false;
      };

      const getRequestDataForCheck = async (checkDocRef) => {
        for (const name of requestDataSubcollectionNames) {
          try {
            const subref = collection(checkDocRef, name);
            const s = await getDocs(query(subref, limit(1)));
            if (!s.empty) return s.docs[0].data() || {};
          } catch (_) {
            // ignore
          }
        }
        return {};
      };

      const { start, end } = getMonthBounds(month);

      const lower = createdAtType === "iso" ? start.toISOString() : start;
      const upper = createdAtType === "iso" ? end.toISOString() : end;

      const qy = query(
        collectionGroup(db, "Checks"),
        where("createdAt", ">=", lower),
        where("createdAt", "<", upper),
        orderBy("createdAt", "asc"),
      );

      const snap = await getDocs(qy);

      const rowsRaw = snap.docs.map((docSnap) => {
        const data = docSnap.data() || {};
        const ownerEmail = docSnap.ref.parent?.parent?.id || "";

        const createdAtIso =
          typeof data.createdAt === "string"
            ? data.createdAt
            : (data.createdAt?.toDate?.()?.toISOString?.() ?? "");

        const typeNorm = normaliseCheckType(data.checkType);
        const price = getCheckPrice(typeNorm);

        return {
          ...data,
          docId: docSnap.id,
          __ownerEmail: ownerEmail,
          createdAtIso,
          __ref: docSnap.ref,
          __checkTypeNorm: typeNorm,
          __price: price,
        };
      });

      if (!rowsRaw.length) {
        alert("No checks found for that month.");
        return;
      }

      const experianSubcollectionNames = [
        "ExperianResponse",
        "Experian Response",
        "experianResponse",
        "experian response",
      ];

      const included = [];
      for (const r of rowsRaw) {
        let include = false;

        if (isSimpleOrPepsCheck(r)) {
          include = true;
        } else {
          const hasSessionId =
            r.sessionId !== undefined &&
            r.sessionId !== null &&
            String(r.sessionId).trim() !== "";

          include = hasSessionId;

          if (!include) {
            const hasExperian = await hasSubcollection(
              r.__ref,
              experianSubcollectionNames,
            );
            include = hasExperian;
          }
        }

        if (include) included.push(r);
      }

      if (!included.length) {
        alert("No checks found for that month.");
        return;
      }

      // Sort by Summary key (forAdviser first else owner), then date
      const sorted = [...included].sort((a, b) => {
        const ea = (getSummaryKeyEmail(a) || "").toLowerCase();
        const eb = (getSummaryKeyEmail(b) || "").toLowerCase();
        if (ea !== eb) return ea.localeCompare(eb);

        const da = new Date(a.createdAtIso || a.createdAt || 0).getTime();
        const dbt = new Date(b.createdAtIso || b.createdAt || 0).getTime();
        return da - dbt;
      });

      const enriched = await Promise.all(
        sorted.map(async (r) => {
          const rd = await getRequestDataForCheck(r.__ref);
          return { ...r, __requestData: rd };
        }),
      );

      const wb = buildStatementWorkbook(enriched, { month, splitByType, mapRow });

      const wbout = await wb.xlsx.writeBuffer();
      const blob = new Blob([wbout], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      saveAs(
        blob,
        `${filenamePrefix} - ${dayjs(month).format("MMMM YYYY")}.xlsx`,
      );
    } catch (e) {
      console.error("Export error:", e);
      alert(
        e?.message?.includes("index")
          ? "Firestore needs a collection-group index on Checks.createdAt (ASC). Check console for the link."
          : "Failed to export. See console for details.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <ButtonGreen
      onClick={handleDownload}
      disabled={loading}
      buttonText={loading ? "Downloading…" : "Download Excel"}
    />
  );
}
